import styles from './numInput.module.scss';
import formStyles from '../formStyles.module.scss';
/**
 * Number input field with an associated label for accessible form entry.
 *
 * @remarks
 * This is a controlled input. The value is managed via the `value` prop and updated with `onChange`.
 * The `onChange` callback provides the current input value and the native change event for flexibility. You can then use the value to update your form state as needed or run any other function onchange.
 * Additional native `<input>` props (e.g. `min`, `max`, `step`) can be passed via `...rest`.
 *
 * If you provide `helpText` or `errorMessage`, they are programmatically associated via `aria-describedby`.
 * Providing an `errorMessage` marks the field invalid (`aria-invalid`) and renders the error text.
 * When `showRequired` is false, the input remains accessible via `aria-required`.
 *
 * @param props.id - Unique input ID used to associate the label
 * @param props.name - Field name used for form submission/validation
 * @param props.title - Visible label text
 * @param props.min Optional - Minimum value allowed
 * @param props.max Optional - Maximum value allowed
 * @param props.value - Current value of the input
 * @param props.onChange Optional - (value, e?) => {someFunc(value)}. Value is the current input value, e is the native change event if you need it. SomeFunc is the function you want to call with the new value.
 * @param props.onBlur Optional - (value, e?) => {someFunc(value)}. Value is the current input value, e is the native blur event if you need it. SomeFunc is the function you want to call with the new value on blur.
 * @param props.required Optional - Marks the field required (default: false)
 * @param props.showRequired Optional - Controls whether the required asterisk (*) is shown
 * @param props.helpText Optional - Helper text shown under the label
 * @param props.errorMessage Optional - Error message shown under the input
 * @param props.disabled Optional - Disables the input
 * @param props.formInstructionsId Optional - ID for additional accessibility description
 * @param props.rest - Any additional native `<input>` attributes
 *
 * @example
 * ```tsx
 * <NumInput
 *   id="guests"
 *   name="guests"
 *   title="Number of Guests"
 *   value={form.guests}
 *   onChange={(guestsValue) => setForm({ ...form, guests: guestsValue })}
 *   min={1}
 *   max={200}
 * />
 * ```
 */
const NumInput = ({
    id,
    name,
    title,
    min,
    max,
    value,
    onChange,
    onBlur,
    required = false,
    showRequired = true,
    helpText,
    errorMessage,
    disabled,
    formInstructionsId,
    ...rest
}: {
    id: string;
    name: string;
    title: string;
    min?: number;
    max?: number;
    value: string;
    onChange?: (value: string, e?: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (value: string, e?: React.FocusEvent<HTMLInputElement>) => void;
    required?: boolean;
    showRequired?: boolean;
    helpText?: string;
    errorMessage?: string;
    disabled?: boolean;
    formInstructionsId?: string;
} & Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'onBlur' | 'value' | 'type'
>) => {
    const helpId = `${id}-help`;
    const errorId = `${id}-error`;
    const asterisk = (
        <span
            aria-hidden={!showRequired}
            className={formStyles.requiredAsterisk}
        >
            {' '}
            *
        </span>
    );
    const describedBy = [
        helpText ? helpId : null,
        errorMessage ? errorId : null,
        formInstructionsId || null,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            data-testid="numinput"
            className={`${formStyles.inputcontainer} ${styles.numWidth}`}
        >
            <label
                data-testid="numinputlabel"
                htmlFor={id}
                className={formStyles.label}
            >
                {title}
                {required && showRequired ? asterisk : ''}
            </label>

            {helpText && (
                <p id={helpId} className={formStyles.help}>
                    {helpText}
                </p>
            )}

            <input
                data-testid="numinputinput"
                type="number"
                id={id}
                name={name}
                min={min}
                max={max}
                value={value}
                onChange={(e) => onChange?.(e.target.value, e)}
                onBlur={(e) => onBlur?.(e.target.value, e)}
                required={required}
                disabled={disabled}
                aria-invalid={Boolean(errorMessage) || undefined}
                aria-describedby={describedBy || undefined}
                aria-required={required || undefined}
                className={`${formStyles.input} ${rest.className ?? ''}`}
                {...rest}
            />

            {errorMessage && (
                <p id={errorId} className={formStyles.error} role="alert">
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

export default NumInput;
